import { useEffectWithController } from '@carvana/merch-react-hooks';
import { isSuccess, isFailure } from '@carvana/merch-result';
import { useContext } from 'react';
import { useResolvedOnServer } from '~/context/common/DeferredResolutionContext';
import StaticContext from '~/context/common/StaticContext';
import { useUserLocation, useIsLoggedIn } from '~/context/common/UserContext';
import { useGetIsReturnUser } from '~/context/home/GlobalContext';
import RecentSearchesContext from '~/context/home/RecentSearchesContext';
import useSkipFirstFetchIfResolvedOnServer from '~/hooks/common/useSkipFirstFetchIfResolvedOnServer';
import { fetchRecentSearches } from '~/services/home';
import { BrowserCookies } from '~/utilities';
import { generateErrorLogger } from '~/utilities/loggers';

export default function useGetRecentSearches() {
  const {
    fetchRecentSearchesTrigger,
    recentSearches: { setData, setLoaded, setLoading, setError }
  } = useContext(RecentSearchesContext);
  const { eventCorrelationId } = useContext(StaticContext);
  const isLoggedIn = useIsLoggedIn();
  const { zip } = useUserLocation();
  const isReturningUser = useGetIsReturnUser();
  const { recentSearches: recentSearchesResolvedOnServer } =
    useResolvedOnServer();
  const shouldSkipFetch = useSkipFirstFetchIfResolvedOnServer(
    recentSearchesResolvedOnServer,
    fetchRecentSearchesTrigger
  );

  useEffectWithController(
    controller => {
      const browserCookieId = BrowserCookies.get('BrowserCookieId');

      if (!browserCookieId || !zip || !isReturningUser) {
        setLoaded();

        return;
      }

      if (shouldSkipFetch(fetchRecentSearchesTrigger)) return;

      setLoading();

      fetchRecentSearches(
        {
          browserCookieId,
          correlationId: eventCorrelationId,
          count: 8,
          zip5: zip
        },
        isLoggedIn,
        controller
      )
        .then(result => {
          if (isSuccess(result)) {
            setData(result.content.recentSearches);

            return;
          }

          if (isFailure(result)) {
            setError(new Error(result.errorMessage));
          }

          setData([]);
        })
        .catch(
          generateErrorLogger(
            'Get user recent searches',
            {},
            { callback: setError, controller }
          )
        );
    },
    [
      eventCorrelationId,
      fetchRecentSearchesTrigger,
      isLoggedIn,
      isReturningUser,
      setData,
      setError,
      setLoaded,
      setLoading,
      shouldSkipFetch,
      zip
    ]
  );
}
